type BackButtonEntry = {
	priority: number
	handler: () => void
}

// Handler stack sorted by priority (ascending). Last entry = active handler.
const handlerStack: BackButtonEntry[] = []

const listeners = new Set<() => void>()

let lastCallTime = 0
const DEBOUNCE_MS = 300

function notify() {
	for (const listener of listeners) {
		listener()
	}
}

function insertEntry(entry: BackButtonEntry) {
	// Insert so that stack stays sorted by priority (ascending).
	// Among equal priorities, new entry goes after existing ones (last wins).
	const below = handlerStack.findLastIndex(
		(existing) => existing.priority <= entry.priority,
	)
	handlerStack.splice(below + 1, 0, entry)
}

export function registerHandler(
	handler: () => void,
	priority: number,
): () => void {
	const entry: BackButtonEntry = {
		priority,
		handler,
	}

	insertEntry(entry)
	notify()

	return () => {
		const index = handlerStack.indexOf(entry)
		if (index !== -1) {
			handlerStack.splice(index, 1)
			notify()
		}
	}
}

/**
 * Returns the active handler (highest priority, last registered among equals).
 * Debounced: ignores calls within 300ms of the previous one to prevent double-tap.
 */
export function getActiveHandler(): (() => void) | null {
	const entry = handlerStack[handlerStack.length - 1]
	if (!entry) return null

	return () => {
		const now = Date.now()
		if (now - lastCallTime < DEBOUNCE_MS) return
		lastCallTime = now
		entry.handler()
	}
}

// For useSyncExternalStore
export function subscribe(callback: () => void): () => void {
	listeners.add(callback)
	return () => {
		listeners.delete(callback)
	}
}

export function getSnapshot(): number {
	return handlerStack.length
}
